/**
 * InboxLayout - Split-view layout for WhatsApp inbox
 * Left: Conversation list, Right: Customer profile
 */

import { useState, useCallback } from 'react';
import { ConversationList } from './ConversationList';
import { CustomerProfilePanel } from './CustomerProfilePanel';
import { QRCodeAuth } from './QRCodeAuth';
import { useWhatsApp } from '../../hooks/useWhatsApp';
import { Badge } from '../ui/badge';
import { Wifi, WifiOff } from 'lucide-react';
import type { WhatsAppConversation } from '@leal-crm/shared';

export function InboxLayout() {
  const {
    status,
    qrDataUrl,
    conversations,
    isLoadingStatus,
    isLoadingConversations,
    isLoadingQR,
    customerMatch,
    isLoadingMatch,
    refreshQR,
    selectConversation,
    selectedConversation,
  } = useWhatsApp();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelectConversation = useCallback(
    (conversation: WhatsAppConversation) => {
      setSelectedId(conversation.id);
      selectConversation(conversation);
    },
    [selectConversation]
  );

  // Show QR auth if not connected
  if (status === 'disconnected' || status === 'qr_pending') {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h1 className="text-lg font-semibold">Inbox</h1>
          <Badge variant="destructive" className="flex items-center gap-1">
            <WifiOff className="h-3 w-3" />
            Desconectado
          </Badge>
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <QRCodeAuth
            qrDataUrl={qrDataUrl}
            isLoading={isLoadingQR || isLoadingStatus}
            onRefresh={refreshQR}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4">
        <h1 className="text-lg font-semibold">Inbox</h1>
        <Badge
          variant={status === 'ready' ? 'default' : 'secondary'}
          className="flex items-center gap-1"
        >
          <Wifi className="h-3 w-3" />
          {status === 'ready' ? 'Conectado' : 'Conectando...'}
        </Badge>
      </div>

      {/* Split view */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Conversation list */}
        <div className="w-80 shrink-0 border-r">
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            onSelect={handleSelectConversation}
            isLoading={isLoadingConversations}
          />
        </div>

        {/* Right: Customer profile */}
        <div className="flex-1">
          <CustomerProfilePanel
            match={customerMatch}
            contactPhone={selectedConversation?.contactPhone || ''}
            isLoading={isLoadingMatch}
          />
        </div>
      </div>
    </div>
  );
}
